import { useState } from 'react';
import { X, Share2, ShieldCheck, AlertTriangle, Copy, CheckCircle } from 'lucide-react';
import { patientApi } from '../api';
import Spinner from '../../../components/ui/Spinner';

const INSTITUTION_TYPES = [
  'DOCTOR', 'HOSPITAL', 'LAB', 'PHARMACY', 'AMBULANCE', 'INSURANCE',
  'TPA', 'SCHOOL', 'UNIVERSITY', 'EMPLOYER', 'GOVERNMENT', 'TRAVEL',
  'SPORTS', 'BLOOD_BANK', 'OTHER',
];

const DURATIONS = [
  { label: '1 Hour', hours: 1 },
  { label: '24 Hours', hours: 24 },
  { label: '7 Days', hours: 168 },
  { label: '30 Days', hours: 720 },
];

export default function InstitutionShareModal({
  open, onClose, certificateId, certificateTitle,
}: {
  open: boolean;
  onClose: () => void;
  certificateId: string;
  certificateTitle: string;
}) {
  const [role, setRole] = useState('DOCTOR');
  const [name, setName] = useState('');
  const [institution, setInstitution] = useState('');
  const [hours, setHours] = useState(24);
  const [customHours, setCustomHours] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const handleShare = async () => {
    if (!name.trim()) { setError('Recipient name is required'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await patientApi.shareDocument(certificateId, {
        sharedWithName: name.trim(),
        sharedWithRole: role,
        sharedWithInstitution: institution.trim() || undefined,
        validHours: hours === -1 ? Number(customHours) || 24 : hours,
      });
      setResult(res);
    } catch (e: any) {
      setError(e.message || 'Failed to share');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (result?.shareCode) {
      navigator.clipboard.writeText(result.shareCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Share2 className="w-5 h-5 text-blue-600" /> Share Document
          </h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Consent Warning */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2 text-xs text-amber-800">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Consent Notice</p>
              <p>NeoVerify proves document authenticity. Sharing grants access to this specific document only, not your full health records.</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs text-gray-500">Sharing</p>
            <p className="text-sm font-semibold text-gray-900 truncate">{certificateTitle}</p>
          </div>

          {!result ? (
            <>
              <div>
                <label className="label">Institution Type</label>
                <select className="input" value={role} onChange={e => setRole(e.target.value)}>
                  {INSTITUTION_TYPES.map(t => (
                    <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Recipient Name *</label>
                <input className="input" placeholder="Dr. Sharma, City Hospital..." value={name} onChange={e => setName(e.target.value)} />
              </div>

              <div>
                <label className="label">Institution Name</label>
                <input className="input" placeholder="Optional" value={institution} onChange={e => setInstitution(e.target.value)} />
              </div>

              <div>
                <label className="label">Access Duration</label>
                <div className="grid grid-cols-2 gap-2">
                  {DURATIONS.map(d => (
                    <button key={d.hours} onClick={() => setHours(d.hours)}
                      className={`text-sm py-2 rounded-lg border transition-colors ${hours === d.hours ? 'bg-teal-50 border-teal-300 text-teal-700 font-semibold' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                      {d.label}
                    </button>
                  ))}
                </div>
                <button onClick={() => setHours(-1)} className={`mt-2 text-xs ${hours === -1 ? 'text-teal-600 font-semibold' : 'text-gray-400'}`}>
                  Custom duration
                </button>
                {hours === -1 && (
                  <input className="input mt-1" type="number" placeholder="Hours" value={customHours} onChange={e => setCustomHours(e.target.value)} />
                )}
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}

              <button onClick={handleShare} disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
                {loading ? <Spinner size="sm" /> : <><ShieldCheck className="w-4 h-4" /> Share with Consent</>}
              </button>
            </>
          ) : (
            <div className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                <CheckCircle className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-emerald-800">Document shared successfully</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Share Code</span>
                  <button onClick={copyCode} className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1">
                    {copied ? <><CheckCircle className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                  </button>
                </div>
                <p className="font-mono text-sm text-gray-900 break-all">{result.shareCode}</p>
                <p className="text-[10px] text-gray-400">
                  Valid until: {new Date(result.validUntil).toLocaleString('en-IN')}
                </p>
              </div>

              <button onClick={onClose} className="btn-primary w-full">Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
